import { useMemo, useRef } from "react";

import { isApiClientError, isValidationApiError } from "../api/errors";
import { newIdempotencyKey } from "../api/idempotency";

export type CounterpartiesTab = "counterparties" | "rules" | "inbox";

export const counterpartiesTabs: readonly CounterpartiesTab[] = [
  "counterparties",
  "rules",
  "inbox",
];

export interface CounterpartiesSearch {
  tab: CounterpartiesTab;
  /** Поиск по названию и ИНН. */
  q: string;
  /** Текущая страница списка контрагентов (нумерация с 1). */
  page: number;
  /** Открытый контрагент или `new` для формы создания. */
  id?: string;
  /** Письмо, открытое в очереди разбора. */
  message?: string;
}

export const newCounterpartyId = "new";

/** Значения из адресной строки недоверенные: всё, что не подошло, отбрасывается. */
export function validateCounterpartiesSearch(
  value: Record<string, unknown>,
): CounterpartiesSearch {
  const tab = counterpartiesTabs.find((item) => item === value.tab);
  const text = (candidate: unknown): string | undefined =>
    typeof candidate === "string" && candidate.trim() !== ""
      ? candidate.trim()
      : undefined;
  const id = text(value.id);
  const message = text(value.message);
  const page = Number(value.page);
  return {
    tab: tab ?? "counterparties",
    q: typeof value.q === "string" ? value.q.slice(0, 255) : "",
    page: Number.isInteger(page) && page > 0 ? page : 1,
    ...(id === undefined ? {} : { id }),
    ...(message === undefined ? {} : { message }),
  };
}

/** Из адресной строки убираются значения по умолчанию: ссылка остаётся короткой. */
export function compactCounterpartiesSearch(
  search: CounterpartiesSearch,
): Partial<CounterpartiesSearch> {
  return {
    ...(search.tab === "counterparties" ? {} : { tab: search.tab }),
    ...(search.q === "" ? {} : { q: search.q }),
    ...(search.page === 1 ? {} : { page: search.page }),
    ...(search.id === undefined ? {} : { id: search.id }),
    ...(search.message === undefined ? {} : { message: search.message }),
  };
}

/**
 * Ключ идемпотентности одного намерения. Пока человек не менял данные, повтор
 * после сбоя уходит с тем же ключом и не создаёт дубль; после успеха или
 * правки данных ключ сбрасывается и следующая отправка получает новый.
 */
export function useSubmissionKey() {
  const stored = useRef<string | null>(null);
  return useMemo(
    () => ({
      current(): string {
        stored.current ??= newIdempotencyKey();
        return stored.current;
      },
      reset(): void {
        stored.current = null;
      },
    }),
    [],
  );
}

/** Ошибки полей, которые сервер вернул в `error.details.fields`. */
export function serverFieldErrors(error: unknown): Record<string, string> {
  if (!isValidationApiError(error)) return {};
  const messages: Record<string, string> = {};
  for (const [field, values] of Object.entries(error.details.fields)) {
    const first = values[0];
    if (first === undefined) continue;
    // `domains.2` и `contacts.0.email` относятся к полю целиком.
    messages[field.replace(/\..*$/, "")] ??= first;
  }
  return messages;
}

export function requestErrorText(error: unknown, fallback: string): string {
  if (isValidationApiError(error)) {
    return "Сервер не принял данные. Проверьте отмеченные поля.";
  }
  if (isApiClientError(error)) {
    if (error.status === 403) return "Недостаточно прав для этого действия.";
    if (error.status === 404)
      return "Запись не найдена: возможно, её уже удалили. Обновите список.";
    if (error.status === 409)
      return "Действие не выполнено. Проверьте, что в поле «Рабочая организация» выбрана конкретная организация, а не «Все организации»; если выбрана — обновите страницу и повторите.";
    if (error.status === 429)
      return "Слишком много запросов. Подождите немного и повторите.";
    if (error.status === 503)
      return "Сервис временно недоступен. Ничего не потеряно — повторите позже.";
    if (error.code === "document_workflow.module_disabled")
      return "Модуль документооборота отключён.";
  }
  return fallback;
}

export function formatDate(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsed);
}
